// src/pages/Interviews/InterviewsListPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Drawer,
    Grid,
    Group,
    Loader,
    Text,
    Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';

import { useBodyClass } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import {
    loadVideos,
    selectAllVideos,
    selectVideosError,
    selectVideosStatus,
} from '@/features/videos/videosSlice';

import { loadGuests, selectAllGuests, selectGuestsStatus } from '@/features/guests/guestsSlice';
import type { Guest } from '@/types/guest';

import { SearchBar } from '@/components/SearchBar/SearchBar';
import { VideoItemsList } from '@/components/Video/VideoItemsList/VideoItemsList';
import { VideoListPagination } from '@/components/Video/VideoItemsList/VideoListPagination';

import { GuestsFilterSidebar } from '@/components/Interviews/GuestsFilterSidebar';

import type { VideoItemData, VideoRecord } from '@/types/video';
import { toItem } from '@/features/videos/videoNormalize';

import classes from '../Video/VideoPages.module.scss';

const parseCsvParam = (v: string | null): string[] =>
    v?.trim() ? v.split(',').map((s) => s.trim()).filter(Boolean) : [];

const toCsvParam = (arr: string[]) => (arr.length ? arr.join(',') : undefined);

const hasGuests = (v: VideoRecord): boolean =>
    Array.isArray(v.podcast_guest_ids) && v.podcast_guest_ids.length > 0;

export const InterviewsListPage: React.FC = () => {
    useBodyClass(classes.videosPage);

    const [opened, { toggle, close }] = useDisclosure(false);

    const dispatch = useAppDispatch();

    const status = useAppSelector(selectVideosStatus);
    const error = useAppSelector(selectVideosError);

    const guestsStatus = useAppSelector(selectGuestsStatus);

    // URL params: q + guests
    const [params, setParams] = useSearchParams();
    const urlQuery = params.get('q') ?? '';
    const urlGuests = parseCsvParam(params.get('guests'));

    // Local UI state
    const [query, setQuery] = useState<string>(urlQuery);
    const [selectedGuests, setSelectedGuests] = useState<string[]>(urlGuests);

    const [searchResults, setSearchResults] = useState<VideoItemData[]>([]);
    const [currentItems, setCurrentItems] = useState<VideoItemData[]>([]);
    const [currentSearch, setCurrentSearch] = useState<string>(urlQuery);

    // Load datasets
    useEffect(() => {
        if (status === 'idle') void dispatch(loadVideos());
    }, [status, dispatch]);

    useEffect(() => {
        if (guestsStatus === 'idle') void dispatch(loadGuests());
    }, [guestsStatus, dispatch]);

    const allRecords = useAppSelector(selectAllVideos) as VideoRecord[];
    const allGuests = useAppSelector(selectAllGuests) as Guest[];

    const interviewRecords = useMemo(
        () => allRecords.filter(hasGuests),
        [allRecords]
    );


    const availableGuestIds = useMemo(() => {
        const set = new Set<number>();
        for (const v of interviewRecords) {
            for (const gid of v.podcast_guest_ids ?? []) set.add(gid);
        }
        return set;
    }, [interviewRecords]);


    const guestOptions = useMemo(() => {
        return allGuests
            .filter((g) => availableGuestIds.has(g.id))
            .map((g) => ({ value: String(g.id), label: g.name }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [allGuests, availableGuestIds]);


    const filteredByGuests = useMemo(() => {
        if (!selectedGuests.length) return interviewRecords;

        const wanted = new Set(
            selectedGuests
                .map((x) => Number(x))
                .filter((n) => Number.isFinite(n))
        );

        return interviewRecords.filter((v) => {
            const ids = v.podcast_guest_ids ?? [];
            return ids.some((id) => wanted.has(id));
        });
    }, [interviewRecords, selectedGuests]);

    const baseFilteredRecords = useMemo(
        () =>
            filteredByGuests.filter(
                (v) =>
                    !v.upcoming &&
                    ((v.releasedTs ?? v.releaseTs ?? 0) <= Date.now() ||
                        (v.released == null && v.release_date == null))
            ),
        [filteredByGuests]
    );

    const baseFiltered: VideoItemData[] = useMemo(
        () => baseFilteredRecords.map(toItem),
        [baseFilteredRecords]
    );

    // Default search dataset
    useEffect(() => {
        if (baseFiltered.length && !query) setSearchResults(baseFiltered);
    }, [baseFiltered, query]);

    // Final list: search results or base list
    const finalFiltered: VideoItemData[] = useMemo(
        () => (currentSearch ? searchResults : baseFiltered),
        [currentSearch, searchResults, baseFiltered]
    );

    const hasFilters = selectedGuests.length > 0 || query.trim().length > 0;

    const handleResetAll = useCallback(() => {
        setSelectedGuests([]);
        setQuery('');
        setCurrentSearch('');
        setSearchResults(baseFiltered);
    }, [baseFiltered]);

    // Sync state → URL (q + guests)
    useEffect(() => {
        const next = new URLSearchParams(params);

        if (query && query.trim()) next.set('q', query.trim());
        else next.delete('q');

        const guestsCsv = toCsvParam(selectedGuests);
        if (guestsCsv) next.set('guests', guestsCsv);
        else next.delete('guests');

        if (next.toString() !== params.toString()) setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, selectedGuests]);

    // URL → state (back/forward)
    useEffect(() => {
        const q = params.get('q') ?? '';
        const g = parseCsvParam(params.get('guests'));

        setQuery(q);
        setCurrentSearch(q);
        setSelectedGuests(g);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.toString()]);

    if (error) return <div>Error: {String(error)}</div>;
    console.log('guests sample', allGuests[0], 'availableGuestIds size', availableGuestIds.size);
    return (
        <section>
            <Container>
                <Box mb="md">
                    <Title order={2}>Interviews</Title>
                    <Text c="dimmed">Episodes with podcast guests</Text>
                </Box>

                <Grid gutter="xl">
                    {/* Sidebar desktop */}
                    <Grid.Col span={{ base: 12, md: 3 }} visibleFrom="md">
                        <GuestsFilterSidebar
                            options={guestOptions}
                            selected={selectedGuests}
                            onChangeSelected={setSelectedGuests}
                            onResetAll={handleResetAll}
                            showReset={hasFilters}
                        />
                    </Grid.Col>

                    {/* Content */}
                    <Grid.Col span={{ base: 12, md: 9 }}>
                        <Group justify="space-between" mb="sm" hiddenFrom="md">
                            <div />
                            <Button
                                onClick={toggle}
                                leftSection={<IconAdjustmentsHorizontal size={18} />}
                                variant="light"
                                aria-label="Open Filters"
                            >
                                Filters
                            </Button>
                        </Group>

                        <SearchBar<VideoItemData>
                            videos={baseFiltered}
                            value={query}
                            onChangeValue={(v) => {
                                setQuery(v);
                                setCurrentSearch(v);
                            }}
                            setSearchResults={setSearchResults}
                        />

                        {status === 'loading' && finalFiltered.length === 0 ? (
                            <Loader />
                        ) : (
                            <>
                                {status === 'succeeded' && finalFiltered.length === 0 && (
                                    <Text c="dimmed" ta="center" mt="xl">
                                        No interviews found
                                    </Text>
                                )}

                                <VideoItemsList items={currentItems} />

                                <VideoListPagination
                                    data={finalFiltered}
                                    itemsPerPage={25}
                                    setCurrentItems={setCurrentItems}
                                    currentSearch={currentSearch}
                                />
                            </>
                        )}
                    </Grid.Col>
                </Grid>
            </Container>

            {/* Mobile drawer */}
            <Drawer
                opened={opened}
                onClose={close}
                position="left"
                size="100%"
                padding="md"
                hiddenFrom="md"
                aria-label="Filter guests"
                overlayProps={{ opacity: 0.55, blur: 2 }}
                lockScroll
                withCloseButton
                withinPortal
            >
                <GuestsFilterSidebar
                    options={guestOptions}
                    selected={selectedGuests}
                    onChangeSelected={setSelectedGuests}
                    onResetAll={handleResetAll}
                    showReset={hasFilters}
                />

                <Group justify="flex-end" mt="md">
                    <Button onClick={close} variant="filled">
                        Apply
                    </Button>
                </Group>
            </Drawer>
        </section>
    );
};
